import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { listLocations, createLocation, updateLocation, deleteLocation } from "../api/locationsAdmin";

const LANGS = [
  ["ru", "Русский"],
  ["kz", "Қазақша"],
  ["en", "English"],
  ["zh", "中文"],
  ["ky", "Кыргызча"],
  ["uz", "Oʻzbekcha"],
];

const KINDS = {
  countries: { one: "страну", title: "Страны", icon: GlobeAltIcon },
  regions: { one: "область", title: "Области", icon: MapIcon },
  districts: { one: "район", title: "Районы", icon: MapPinIcon },
};

const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

const nameOf = (item) => item?.name_ru || item?.name_kz || item?.name_en || `#${item?.id}`;

const Column = ({ kind, items, loading, selectedId, onSelect, onAdd, onEdit, onDelete, disabled, emptyText, drillable }) => {
  const [query, setQuery] = useState("");
  const meta = KINDS[kind];
  const Icon = meta.icon;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => LANGS.some(([l]) => (i[`name_${l}`] || "").toLowerCase().includes(q)) || String(i.id) === q.replace("#", ""));
  }, [items, query]);

  return (
    <section className={`flex min-h-[28rem] flex-col overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm ${disabled ? "opacity-50" : ""}`}>
      <div className="flex items-center justify-between gap-2 border-b border-border-primary px-4 py-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <Icon className="h-4 w-4 text-[#7C3AED]" />
          {meta.title}
          <span className="rounded-full bg-bg-tertiary px-1.5 text-xs tabular-nums text-text-muted">{disabled ? "—" : items.length}</span>
        </h3>
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-lg border border-border-primary px-2 py-1 text-xs font-medium text-text-secondary hover:bg-bg-tertiary hover:text-text-primary disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-3.5 w-3.5" /> Добавить
        </button>
      </div>
      {!disabled && items.length > 6 && (
        <div className="relative border-b border-border-primary">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск на любом языке или #ID"
            aria-label={`Поиск: ${meta.title}`}
            className="w-full bg-transparent py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
          />
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {disabled ? (
          <p className="px-4 py-10 text-center text-sm text-text-muted">{emptyText}</p>
        ) : loading && items.length === 0 ? (
          <ul className="divide-y divide-border-primary">
            {Array.from({ length: 6 }).map((_, i) => (
              <li key={i} className="animate-pulse space-y-1.5 px-4 py-2.5">
                <div className="h-3 w-2/3 rounded bg-bg-tertiary" />
                <div className="h-2.5 w-1/3 rounded bg-bg-tertiary" />
              </li>
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-text-muted">{items.length ? "Ничего не найдено" : "Пусто"}</p>
        ) : (
          <ul className="divide-y divide-border-primary">
            {filtered.map((item) => {
              const selected = selectedId === item.id;
              return (
                <li key={item.id} className="group relative">
                  <button
                    type="button"
                    onClick={() => onSelect?.(item)}
                    className={`flex w-full items-center gap-2 px-4 py-2.5 pr-20 text-left transition-colors ${selected ? "bg-[#4F45E5]/10" : "hover:bg-bg-tertiary/60"} ${onSelect ? "" : "cursor-default"}`}
                  >
                    {selected && <span className="absolute inset-y-0 left-0 w-1 bg-accent-grad" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-text-primary">
                        {nameOf(item)}
                        {item.code && <span className="ml-1.5 rounded bg-bg-tertiary px-1 text-[10px] font-semibold uppercase text-text-muted">{item.code}</span>}
                      </span>
                      <span className="block truncate text-xs text-text-muted">
                        <span className="font-mono">#{item.id}</span>
                        {item.name_kz && item.name_kz !== item.name_ru && ` · ${item.name_kz}`}
                      </span>
                    </span>
                    {drillable && <ChevronRightIcon className={`h-4 w-4 shrink-0 ${selected ? "text-[#7C3AED]" : "text-text-muted"}`} />}
                  </button>
                  <div className="absolute right-8 top-1/2 flex -translate-y-1/2 gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <button type="button" onClick={() => onEdit(item)} title="Редактировать" aria-label="Редактировать" className="rounded-md bg-bg-card p-1.5 text-text-muted shadow-sm hover:text-text-primary">
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => onDelete(item)} title="Удалить" aria-label="Удалить" className="rounded-md bg-bg-card p-1.5 text-text-muted shadow-sm hover:text-rose-500">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

const Editor = ({ kind, item, parents, parentId, onClose, onSaved }) => {
  const editing = Boolean(item?.id);
  const parentKey = kind === "regions" ? "country_id" : kind === "districts" ? "region_id" : null;
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(LANGS.map(([l]) => [`name_${l}`, item?.[`name_${l}`] || ""])),
    code: item?.code || "",
    latitude: item?.latitude ?? "",
    longitude: item?.longitude ?? "",
    ...(parentKey ? { [parentKey]: item?.[parentKey] ?? parentId ?? "" } : {}),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const missing = LANGS.filter(([l]) => !form[`name_${l}`].trim());

  // Пустые переводы можно заполнить русским названием одним нажатием
  const fillFromRu = () =>
    setForm((f) => ({ ...f, ...Object.fromEntries(LANGS.filter(([l]) => !f[`name_${l}`].trim()).map(([l]) => [`name_${l}`, f.name_ru])) }));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const save = async (e) => {
    e.preventDefault();
    if (missing.length) return setError(`Заполните названия: ${missing.map(([, n]) => n).join(", ")}.`);
    if (parentKey && !form[parentKey]) return setError(kind === "regions" ? "Выберите страну." : "Выберите область.");
    const lat = form.latitude === "" ? null : Number(form.latitude);
    const lon = form.longitude === "" ? null : Number(form.longitude);
    if ((lat !== null && !(lat >= -90 && lat <= 90)) || (lon !== null && !(lon >= -180 && lon <= 180))) return setError("Некорректные координаты.");
    const body = Object.fromEntries(LANGS.map(([l]) => [`name_${l}`, form[`name_${l}`].trim()]));
    if (kind === "countries") body.code = form.code.trim().toUpperCase() || null;
    else {
      body[parentKey] = Number(form[parentKey]);
      body.latitude = lat;
      body.longitude = lon;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = editing ? await updateLocation(kind, item.id, body) : await createLocation(kind, body);
      onSaved(saved, `${editing ? "Сохранено" : "Добавлено"}: ${body.name_ru}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form onSubmit={save} role="dialog" aria-modal="true" className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-block border border-border-primary bg-bg-card text-left shadow-lg">
        <div className="flex items-center justify-between border-b border-border-primary px-5 py-3.5">
          <h3 className="text-base font-semibold text-text-primary">
            {editing ? `Редактировать ${KINDS[kind].one}` : `Добавить ${KINDS[kind].one}`}
            {editing && <span className="ml-2 font-mono text-sm text-text-muted">#{item.id}</span>}
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="rounded-lg p-1 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          {parentKey && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-text-secondary">{kind === "regions" ? "Страна" : "Область"}</span>
              <select value={form[parentKey]} onChange={(e) => set(parentKey, e.target.value)} className={inputCls}>
                <option value="">— Выберите —</option>
                {parents.map((p) => (
                  <option key={p.id} value={p.id}>{nameOf(p)}</option>
                ))}
              </select>
              {editing && Number(form[parentKey]) !== item[parentKey] && (
                <span className="mt-1 block text-xs text-amber-500">{kind === "regions" ? "Область" : "Район"} будет перенесён в другой {kind === "regions" ? "стране" : "области"}.</span>
              )}
            </label>
          )}

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-medium text-text-secondary">Название на всех языках приложения</span>
              {missing.length > 0 && form.name_ru.trim() && (
                <button type="button" onClick={fillFromRu} className="text-xs font-medium text-[#7C3AED] hover:underline dark:text-indigo-300">
                  Заполнить пустые из русского
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {LANGS.map(([l, label], i) => (
                <label key={l} className="block">
                  <span className="mb-1 flex items-center gap-1 text-[11px] text-text-muted">
                    {label}
                    {!form[`name_${l}`].trim() && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                  </span>
                  <input autoFocus={i === 0} value={form[`name_${l}`]} onChange={(e) => set(`name_${l}`, e.target.value)} className={inputCls} />
                </label>
              ))}
            </div>
          </div>

          {kind === "countries" ? (
            <label className="block max-w-[10rem]">
              <span className="mb-1 block text-xs font-medium text-text-secondary">Код страны</span>
              <input value={form.code} maxLength={3} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="KZ" className={`${inputCls} font-mono uppercase`} />
            </label>
          ) : (
            <div>
              <span className="mb-1 block text-xs font-medium text-text-secondary">Центр на карте — для глобуса аналитики и поиска рядом</span>
              <div className="flex gap-3">
                <input type="number" step="any" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} placeholder="Широта, напр. 43.238" aria-label="Широта" className={inputCls} />
                <input type="number" step="any" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} placeholder="Долгота, напр. 76.889" aria-label="Долгота" className={inputCls} />
                {form.latitude !== "" && form.longitude !== "" && (
                  <a
                    href={`https://maps.google.com/?q=${form.latitude},${form.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center rounded-lg border border-border-primary px-3 text-sm text-text-secondary hover:bg-bg-tertiary"
                  >
                    Карта ↗
                  </a>
                )}
              </div>
            </div>
          )}

          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border-primary px-5 py-3">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
            Отмена
          </button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            {editing ? "Сохранить" : "Добавить"}
          </button>
        </div>
      </form>
    </div>
  );
};

const DeleteDialog = ({ kind, item, onClose, onDeleted }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const name = nameOf(item);
  const consequences = {
    countries: ["все области и районы этой страны будут удалены", "у объявлений, заявок и адресов пользователей пропадёт страна, область и район"],
    regions: ["все районы области будут удалены", "у объявлений, заявок и адресов пользователей в этой области пропадут область и район", "данные области исчезнут из аналитики по регионам"],
    districts: ["у объявлений, заявок и адресов пользователей в этом районе пропадёт район", "зоны доставки бизнеса по району перестанут работать"],
  }[kind];

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteLocation(kind, item.id);
      onDeleted(`Удалено: ${name}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-block border border-border-primary bg-bg-card p-5 text-left shadow-lg">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-500">
            <TrashIcon className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-base font-semibold text-text-primary">Удалить «{name}»?</h3>
            <p className="mt-1 text-sm text-text-secondary">Это необратимо. Вместе с удалением:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-text-secondary">
              {consequences.map((c) => (
                <li key={c}>{c};</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-text-muted">Чтобы исправить опечатку или перевод, используйте «Редактировать».</p>
          </div>
        </div>
        <label className="mt-4 block">
          <span className="mb-1 block text-xs text-text-secondary">
            Введите <b className="text-text-primary">{name}</b> для подтверждения
          </span>
          <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} className={inputCls} />
        </label>
        {error && <p className="mt-2 text-sm text-rose-500">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
            Отмена
          </button>
          <button
            type="button"
            onClick={run}
            disabled={busy || typed.trim() !== name}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {busy && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            Удалить навсегда
          </button>
        </div>
      </div>
    </div>
  );
};

const LocationManagement = () => {
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState({ countries: true, regions: false, districts: false });
  const [country, setCountry] = useState(null);
  const [region, setRegion] = useState(null);
  const [editor, setEditor] = useState(null); // { kind, item }
  const [toDelete, setToDelete] = useState(null); // { kind, item }
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async (kind, params, setter) => {
    setLoading((l) => ({ ...l, [kind]: true }));
    try {
      const list = await listLocations(kind, params);
      setter([...list].sort((a, b) => nameOf(a).localeCompare(nameOf(b), "ru")));
    } catch (err) {
      setter([]);
      setToast({ type: "error", text: `Не удалось загрузить: ${err.message}` });
    } finally {
      setLoading((l) => ({ ...l, [kind]: false }));
    }
  }, []);

  const loadCountries = useCallback(() => load("countries", {}, setCountries), [load]);
  const loadRegions = useCallback(() => country && load("regions", { country_id: country.id }, setRegions), [load, country]);
  const loadDistricts = useCallback(() => region && load("districts", { region_id: region.id }, setDistricts), [load, region]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  // Казахстан открываем сразу — почти вся работа там
  useEffect(() => {
    if (!country && countries.length) setCountry(countries.find((c) => (c.code || "").toUpperCase() === "KZ") || countries[0]);
  }, [countries, country]);

  useEffect(() => {
    setRegions([]);
    setRegion(null);
    loadRegions();
  }, [loadRegions]);

  useEffect(() => {
    setDistricts([]);
    loadDistricts();
  }, [loadDistricts]);

  const reload = (kind) => (kind === "countries" ? loadCountries() : kind === "regions" ? loadRegions() : loadDistricts());

  const afterSave = (kind, saved, message) => {
    setEditor(null);
    setToast({ type: "success", text: message });
    reload(kind);
    if (kind === "countries" && country?.id === saved?.id) setCountry(saved);
    if (kind === "regions" && region?.id === saved?.id) setRegion(saved);
  };

  const afterDelete = (kind, item, message) => {
    setToDelete(null);
    setToast({ type: "success", text: message });
    if (kind === "countries" && country?.id === item.id) setCountry(null);
    if (kind === "regions" && region?.id === item.id) setRegion(null);
    reload(kind);
  };

  const columnProps = (kind) => ({
    kind,
    onAdd: () => setEditor({ kind, item: null }),
    onEdit: (item) => setEditor({ kind, item }),
    onDelete: (item) => setToDelete({ kind, item }),
    loading: loading[kind],
  });

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Локации</h2>
          <p className="mt-0.5 text-sm text-text-secondary">Справочник стран, областей и районов — используется в объявлениях, адресах, фильтрах и аналитике</p>
        </div>
        <nav aria-label="Путь" className="flex items-center gap-1 text-sm text-text-secondary">
          <span className={country ? "text-text-primary" : ""}>{country ? nameOf(country) : "Страна"}</span>
          <ChevronRightIcon className="h-3.5 w-3.5" />
          <span className={region ? "text-text-primary" : ""}>{region ? nameOf(region) : "Область"}</span>
        </nav>
      </div>

      {toast && (
        <div
          role={toast.type === "error" ? "alert" : "status"}
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${toast.type === "error" ? "border-rose-500/30 bg-rose-500/10 text-rose-500" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-500"}`}
        >
          <span>{toast.text}</span>
          <button type="button" onClick={() => setToast(null)} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Column {...columnProps("countries")} items={countries} selectedId={country?.id} onSelect={setCountry} drillable />
        <Column {...columnProps("regions")} items={regions} selectedId={region?.id} onSelect={setRegion} drillable disabled={!country} emptyText="Выберите страну" />
        <Column {...columnProps("districts")} items={districts} disabled={!region} emptyText="Выберите область" />
      </div>

      {editor && (
        <Editor
          kind={editor.kind}
          item={editor.item}
          parents={editor.kind === "regions" ? countries : regions}
          parentId={editor.kind === "regions" ? country?.id : region?.id}
          onClose={() => setEditor(null)}
          onSaved={(saved, message) => afterSave(editor.kind, saved, message)}
        />
      )}

      {toDelete && (
        <DeleteDialog
          kind={toDelete.kind}
          item={toDelete.item}
          onClose={() => setToDelete(null)}
          onDeleted={(message) => afterDelete(toDelete.kind, toDelete.item, message)}
        />
      )}
    </div>
  );
};

export default LocationManagement;
